import {
  type CanvasElementId,
  type ColumnId,
  type StyleConfigProps,
  type VisibilityElementProps,
} from '@blaze-runtime/engine-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ContainerActionConfig,
  type ElementLabelProps,
  type ElementTitleStyleProps,
  type ExpressionObj,
  type FontOptions,
  type FormContainerModelField,
  type PropertiesConfigInputAlignModel,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisibilityModel,
  type PropertiesConfigInputVisualEffectModel,
  type WizardActionType,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

export type WizardStepType = 'data' | 'review' | 'confirmation';
export type WizardConfirmationType = 'Page' | 'Button' | 'Message';
export type WizardMode = 'form' | 'questionnaire';

/** Typography of a questionnaire-generated Text element (question/explanation). */
export type WizardTextStyleProps = {
  font: FontOptions;
  textColor: PropertiesConfigInputColorModel;
};

/** Progress Bar percentage readout - rendered only when the stepper style is `bar`. */
export type WizardProgressPercentProps = {
  enabled?: boolean;
  position?: 'right' | 'left';
};

/**
 * Props path of the steps list - shared by both wizard modes; also used by editor-side
 * effects and knobs that react to updates of this path.
 */
export const WIZARD_STEPS_ITEMS_PATH = 'content.steps.items';

/**
 * Props path of the mode. It sits outside `content.layout` on purpose: that subtree is part
 * of the style sheet (see the wizard definition's `styleIncludePaths`), and applying a sheet
 * must never switch a configured wizard between its two modes.
 */
export const WIZARD_MODE_PATH = 'content.mode';

export type WizardStep = {
  id: string;
  type: WizardStepType;
  label: string;
  required?: boolean;
  reviewForms?: CanvasElementId[];
  postAction?: {
    actionType?: WizardConfirmationType;
    page?: string;
    title?: string;
    message?: string;
    buttonLabel?: string;
    image?: string;
  };
  visible?: PropertiesConfigInputVisibilityModel;
  visibilityExpression?: ExpressionObj[];
  nextAction?: ContainerActionConfig;
  /** Questionnaire mode: the dataset column this question asks about. */
  columnKey?: string;
  /**
   * Questionnaire mode: whether this question shows an explanation under it. Regeneration
   * creates the explanation Text element while this is true and removes it when it is not,
   * so the element's existence is generated from props and never inferred from its text -
   * clearing the text on canvas leaves the element in place.
   */
  explanationEnabled?: boolean;
  /**
   * Questionnaire mode: ids of the generated Text elements inside the question's form that
   * carry the question and its explanation. The elements own their text outright (the
   * panel's Question and Explanation knobs edit them directly); the step only records which
   * element is which, the way a form records its fields in wrapper refs.
   */
  questionElementId?: CanvasElementId;
  explanationElementId?: CanvasElementId;
  /**
   * Questionnaire mode: id of the generated field element bound to the question's column,
   * recorded by every regeneration - the panel's Placeholder knob edits that element.
   */
  fieldElementId?: CanvasElementId;
  /**
   * Questionnaire mode: id of the slot behind this step. Saved so which step owns which
   * slot, and in what order, can always be rebuilt from props alone (step N maps to slot N).
   */
  slotId?: ColumnId;
};

type PropsModel = CanvasElementPropertiesWithData & StyleConfigProps & VisibilityElementProps;

export type WizardPropsModel = PropsModel & {
  content: {
    action?: WizardActionType;
    mode: WizardMode;
    model: FormContainerModelField[];
    layout: {
      style: 'numbers' | 'lines' | 'tabs' | 'bar';
      placement: 'top' | 'side';
      align: PropertiesConfigInputAlignModel;
      numerateTabs?: boolean;
      progressPercent?: WizardProgressPercentProps;
    };
    steps?: {
      items: WizardStep[];
    };
    /** Questionnaire mode: Add Question dialog mirror - column keys with a question step */
    questionsEnabled?: string[];
    saveDraftEnabled?: boolean;
    title: ElementLabelProps;
  };
  style: {
    general: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      /** Border around element */
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
    };
    title: {
      text: PropertiesConfigInputColorModel;
    };
    titleBlock: ElementTitleStyleProps;
    /** Questionnaire mode: typography of the generated question Text elements */
    question?: WizardTextStyleProps;
    /** Questionnaire mode: typography of the generated explanation Text elements */
    explanation?: WizardTextStyleProps;
    buttons: {
      buttonsView: 'primary' | 'secondary';
      primary: {
        text: PropertiesConfigInputColorModel;
        fill: PropertiesConfigInputColorModel;
        outline: PropertiesConfigInputColorModel;
      };
      secondary: {
        text: PropertiesConfigInputColorModel;
        fill: PropertiesConfigInputColorModel;
        outline: PropertiesConfigInputColorModel;
      };
    };
    container: {
      fill: PropertiesConfigInputColorModel;
      /** Border inside element */
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
      radius: BorderRadiusValue;
      visualEffect: PropertiesConfigInputVisualEffectModel;
    };
  };
};
